import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

export default function CaseStudyPage({ project, nextProject }) {
  return (
    <main className="min-h-screen bg-[#240711] px-6 py-8 text-white">
      <div className="mx-auto max-w-6xl">
        <a href="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-emerald-100 transition">
          <ArrowLeft size={16} />
          Back to home
        </a>

        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-20"
        >
          <p className="font-mono text-xs uppercase tracking-[0.45em] text-emerald-100/70">
            {project.category}
          </p>

          <h1 className="mt-6 text-6xl font-black tracking-tight md:text-8xl">
            {project.title}
          </h1>

          <p className="mt-8 max-w-3xl text-xl leading-9 text-white/70">
            {project.summary}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {project.stack.map((item) => (
              <span key={item} className="rounded-full border border-emerald-100/25 bg-white/8 px-4 py-2 text-sm text-emerald-100">
                {item}
              </span>
            ))}
          </div>
        </motion.header>

        {project.heroImage ? (
          <div className="mb-20 rounded-[2rem] overflow-hidden border border-white/10 bg-[#4a1021]">
            <img
              src={project.heroImage}
              alt={`${project.title} screenshot`}
              className="w-full h-auto block"
            />
          </div>
        ) : (
          <div className="mb-20 flex min-h-[420px] items-center justify-center rounded-[2rem] border border-white/10 bg-[#4a1021] text-white/35 text-lg">
            Project mockup / screenshots go here
          </div>
        )}

        <section className="grid gap-8 md:grid-cols-2">
          <InfoBlock title="Overview" content={project.sections.overview} />
          <InfoBlock title="Problem" content={project.sections.problem} />
          <InfoBlock title="My role" content={project.sections.role} />
          <InfoBlock title="Process" content={project.sections.process} />
        </section>

        <section className="my-20 rounded-[2rem] border border-white/10 bg-white/5 p-8">
          <h2 className="text-3xl font-black">Key features</h2>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {project.sections.features.map((feature) => (
              <div key={feature} className="rounded-2xl border border-white/10 bg-black/20 p-5 text-white/70">
                {feature}
              </div>
            ))}
          </div>
        </section>

        <section className="mb-20">
          <InfoBlock title="What I learned" content={project.sections.learning} />
        </section>

        {nextProject && (
          <a
            href={`/case-studies/${nextProject.slug}`}
            className="group mb-10 inline-flex items-center justify-between gap-8 rounded-[2rem] border border-emerald-100/20 bg-[#39101d] hover:border-emerald-100/40 p-8 w-full transition"
          >
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-white/60 opacity-70">
                Next case study
              </p>
              <h2 className="mt-2 text-4xl font-black text-white">{nextProject.title}</h2>
            </div>

            <ArrowUpRight className="text-emerald-100 flex-shrink-0 transition group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        )}
      </div>
    </main>
  );
}

function InfoBlock({ title, content }) {
  return (
    <div className="rounded-[2rem] border border-white/10 bg-white/5 p-7">
      <h2 className="text-2xl font-black">{title}</h2>
      <p className="mt-4 leading-8 text-white/65">{content}</p>
    </div>
  );
}
